/**
 * firebase
 *
 * Initializes Firebase app, Auth (with LOCAL persistence), Firestore, and
 * exports Google Auth Provider. Reads config from env with QA defaults.
 */
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth, browserSessionPersistence, browserLocalPersistence, GoogleAuthProvider, setPersistence } from 'firebase/auth';

// Read config from env for QA/local first; fallback to legacy values
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyDLE4EA6DINDu3RlFI8-a5Yk_dsQwDJiVo",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "advocacy-lab-qa-c41f7.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "advocacy-lab-qa-c41f7",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "advocacy-lab-qa-c41f7.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "1075377647579",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:1075377647579:web:2b0405209fb457bd21c1fe",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-V9RTE2JPYJ"
};

// Firestore named database for QA (fallback to default if not provided)
const firestoreDatabaseId = import.meta.env.VITE_FIRESTORE_DATABASE_ID || 'advocacy-lab-qa';

// SERVER_URL is defined centrally in src/api/core.js

// Initialize Firebase with error handling
let app;
try {
  // Check if Firebase app is already initialized
  if (getApps().length === 0) {
    console.log("Initializing new Firebase app instance");
    app = initializeApp(firebaseConfig);
  } else {
    console.log("Firebase app already initialized, retrieving existing instance");
    app = getApp();
  }
} catch (error) {
  console.error("Error initializing Firebase app:", error);
  // Create a fallback minimal app to prevent crashes
  app = {
    name: "[DEFAULT]",
    options: { ...firebaseConfig },
    automaticDataCollectionEnabled: false
  };
}

// Initialize Auth with error handling
let auth;
try {
  auth = getAuth(app);
  console.log("Firebase auth initialized successfully");
  
  // CRITICAL FIX: Set persistence to LOCAL immediately on initialization
  // This ensures tokens persist across browser sessions and page refreshes
  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      console.log("Firebase persistence set to LOCAL for maximum token persistence");
    })
    .catch((error) => {
      console.error("Error setting Firebase persistence to LOCAL:", error);
      // Fallback to session persistence if LOCAL fails
      setPersistence(auth, browserSessionPersistence)
        .then(() => {
          console.log("Fallback: Firebase persistence set to SESSION");
        })
        .catch((fallbackError) => {
          console.error("Error setting fallback persistence:", fallbackError);
        });
    });

} catch (error) {
  console.error("Error initializing Firebase auth:", error);
  // Create a fallback minimal auth object to prevent crashes
  auth = {
    currentUser: null,
    onAuthStateChanged: (callback) => {
      console.warn("Auth not properly initialized, auth state changes won't be detected");
      callback(null);
      return () => {}; // Return a no-op unsubscribe function
    },
    signInWithPopup: () => Promise.reject(new Error("Auth not properly initialized")),
    signOut: () => Promise.reject(new Error("Auth not properly initialized"))
  };
}

// Create and configure Google Auth Provider with specific scopes
const googleAuthProvider = new GoogleAuthProvider();
// Add necessary scopes for Google Auth
googleAuthProvider.addScope('email');
googleAuthProvider.addScope('profile');
// Set custom parameters for Google Auth that encourage popup behavior and avoid tab opening
googleAuthProvider.setCustomParameters({
  // Remove prompt parameter that can sometimes force tab behavior
  // prompt: 'select_account',
  // Use minimal parameters to encourage popup behavior
  display: 'popup',
  // Ensure we get a consistent popup experience
  ux_mode: 'popup',
  // Add parameter to prefer popup over redirect
  access_type: 'online'
});

// Export needed auth components
export { auth, googleAuthProvider, browserSessionPersistence, browserLocalPersistence, setPersistence };

// Optionally export Firestore instance targeting QA database for modules that need it
export const db = getFirestore(app, firestoreDatabaseId);